import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import type { ProjectParticipantRole } from '../../constants/project-relationships';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { CatalogValueEntity } from './catalog-value.entity';
import { ClientEntity } from './client.entity';
import { JobEntity } from './job.entity';
import { MSmClientOpportunityEntity } from './m-sm-client-opportunity.entity';
import { ProjectEntity } from './project.entity';

/**
 * PD-17109 — a company on a project, with its role there (general contractor,
 * mechanical, plumbing, ...), where it stands (a tenant-editable status that
 * maps to a fixed outcome) and a note in the rep's words.
 *
 * The company is one of the tenant's customers (`fk_client__id`) or a typed
 * name for a company that is not (`companyName`). A customer already takes
 * part through its opportunity or job on the project without a row here; a
 * row adds the role, status and note for it, and may point at that
 * customer's opportunity or job on the project.
 *
 * One row per customer per project (the unique index; a typed company has no
 * customer, so MySQL's NULL handling leaves typed rows to the service, which
 * matches them by name). A row taken off is soft-deleted and comes back on
 * the next add.
 *
 * SM1 exposure: none (a new table).
 */
@Index('project_participant__project_client', ['fkProjectId', 'fkClientId'], { unique: true })
@Index('project_participant__fk_client__id', ['fkClientId'], {})
@Entity('project_participant')
export class ProjectParticipantEntity extends BaseEntity {
	@Column('int', { name: 'fk_project__id', unsigned: true })
	public fkProjectId!: number;

	/** The customer, when the company is one of the tenant's customers. */
	@Column('int', { name: 'fk_client__id', unsigned: true, nullable: true })
	public fkClientId!: number | null;

	/** The company as typed, when it is not a customer. */
	@Column('varchar', { name: 'companyName', length: 255, nullable: true })
	public companyName!: string | null;

	@Column('varchar', { name: 'role', length: 32, default: () => "'other'" })
	public role!: ProjectParticipantRole;

	/** A Project Participant Statuses catalog value. */
	@Column('int', { name: 'fk_catalog_value__id_status', unsigned: true, nullable: true })
	public statusId!: number | null;

	/** That customer's opportunity on this project, when the rep linked it. */
	@Column('int', { name: 'fk_m_sm_client_opportunity__id', unsigned: true, nullable: true })
	public fkOpportunityId!: number | null;

	/** That customer's job on this project, when the rep linked it. */
	@Column('int', { name: 'fk_job__id', unsigned: true, nullable: true })
	public fkJobId!: number | null;

	@Column('varchar', { name: 'note', length: 2000, nullable: true })
	public note!: string | null;

	@ManyToOne(() => ProjectEntity, { onDelete: 'CASCADE', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_project__id', referencedColumnName: 'id' }])
	public project!: ProjectEntity;

	@ManyToOne(() => ClientEntity, { onDelete: 'CASCADE', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public client!: ClientEntity | null;

	@ManyToOne(() => CatalogValueEntity, { onDelete: 'SET NULL', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_catalog_value__id_status', referencedColumnName: 'id' }])
	public status!: CatalogValueEntity | null;

	@ManyToOne(() => MSmClientOpportunityEntity, { onDelete: 'SET NULL', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_m_sm_client_opportunity__id', referencedColumnName: 'id' }])
	public opportunity!: MSmClientOpportunityEntity | null;

	@ManyToOne(() => JobEntity, { onDelete: 'SET NULL', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_job__id', referencedColumnName: 'id' }])
	public job!: JobEntity | null;
}
